import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { environment } from '@config/environment';
import { SupportButtonService } from '@shared/lib/services/support-button.service';

@Component({
  selector: 'ba-support-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if ((supportButtonHidden$ | async) === false) {
      <a
        mat-mini-fab
        color="primary"
        class="support-link"
        [href]="baSupportUrl"
        target="_blank"
        rel="noopener"
        aria-label="Contact Biome Support"
        matTooltip="Contact Biome Support"
        matTooltipPosition="left"
      >
        <mat-icon class="material-symbols-outlined">support_agent</mat-icon>
      </a>
      @if (enableHiding) {
        <button
          mat-icon-button
          class="hide-button"
          aria-label="Hide support button"
          matTooltip="Hide support button"
          matTooltipPosition="above"
          (click)="hideButton()"
        >
          <mat-icon class="material-symbols-outlined">close</mat-icon>
        </button>
      }
    }
  `,
  styles: [
    `
      @use 'styles/variables';
      :host {
        position: absolute;
        right: 25px;
        bottom: 15px;
        z-index: 1;
        display: flex;
      }
      // The global \`a:hover\` rule recolours link text, which would hide the icon.
      .support-link:hover {
        color: variables.$lightest;
      }
      .hide-button {
        --mat-icon-button-state-layer-size: 20px;
        --mat-icon-button-icon-size: 14px;
        --mat-icon-button-touch-target-display: none;
        // The M2 density theme hard-codes a 12px pad sized for a 48px button.
        padding: 3px;
        position: absolute;
        top: -6px;
        left: -6px;
        background-color: variables.$lightest;
        @include variables.box-shadow-md();
        opacity: 0;
        visibility: hidden;
        transition:
          opacity 150ms,
          visibility 150ms;
        mat-icon {
          width: 14px;
          height: 14px;
          font-size: 14px;
        }
      }
      // Not :focus-within — the link keeps focus after a click opens the new tab.
      :host(:hover) .hide-button,
      .support-link:focus-visible + .hide-button,
      .hide-button:focus-visible {
        opacity: 1;
        visibility: visible;
      }
    `,
  ],
  imports: [AsyncPipe, MatIcon, MatIconButton, MatMiniFabButton, MatTooltip],
})
export class SupportButtonComponent {
  @Input() enableHiding = false;
  readonly baSupportUrl = environment.supportDeskUrl;
  readonly supportButtonHidden$ =
    this._supportButtonService.supportButtonHidden$;

  constructor(private readonly _supportButtonService: SupportButtonService) {}

  hideButton() {
    this._supportButtonService.toggle();
  }
}
